import React, {Fragment, useCallback, useEffect, useState} from 'react';
import Box from '../layout/Box';
import TillClosingForm from './forms/TillClosingForm';
import TillClosingList from './lists/TillClosingList';
import BoxEditor from "./BoxEditor";
import Button from "../form/Button";
import {compressArray, hufRound, isEmpty, isFalse} from "../../utils/other";
import is_numeric from "locutus/php/var/is_numeric";
import ComponentEditor, {referenceActionData} from "../form/ComponentEditor/ComponentEditor";
import ContextBased from "./Context/ContextBased";
import {validations, ValidationType} from "../form/ComponentEditor/Validation";
import {postData, sendRequest} from "../../utils/request";
import AbortablePromise from "../../utils/promise/AbortablePromise";
import {CurrencyFormatter} from "../../utils/formatters";
import {list} from "../../utils/array";
import PropsProxy from "../layout/PropsProxy";
import ContentLoader from "../layout/ContentLoader";
import debounce from "../../utils/debounce";
import {empty} from "locutus/php/var";
import context from "./Context/context";
import {loadComponent} from "../../utils/components";

class TillClosingEditor extends React.Component<any, any> {

    state = {
        isLoading: false
    }

    protected editor: any;
    protected list: any;

    protected vouchersRequest: AbortablePromise<object>;
    protected vatrateCorrectionRequest: AbortablePromise<object>;

    componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any) {
        if (this.props.values[this.props.componentConfig.idfield] + "" !== "" + prevProps.values[prevProps.componentConfig.idfield]) {
            this.loadFormData();
        } else if (!this.props.values[this.props.componentConfig.idfield]) {
            if (
                prevProps?.till?.id !== this.props.till?.id
                ||
                this.props.values.till?.id !== this.props.till?.id
                ||
                this.props.values.closings === null
                ||
                this.props.values.closings === undefined
                ||
                this.props.values.vatrate_closings === null
                ||
                this.props.values.vatrate_closings === undefined
            ) {
                this.loadFormData();
            }
        }
    }

    loadForm = async (data) => {
        await this.props.loadForm(data);
        this.editor && this.editor.focus();
    }

    pristineForm = async () => {
        await this.props.pristineForm();
        await this.loadFormData();
        this.editor && this.editor.focus();
    }

    getDateFilters = (props) => {
        const dateFilters = [];
        if(props.values.closing_date) {
            dateFilters.push({field: "transferdate", value: props.values.closing_date, operator: "<="});
        }
        const openCurrentFilter = [];
        const closing_date = props.values.before_closing_date || props.lastClosingDate || "1970-01-01 00:00:00";
        dateFilters.push({field: "transferdate", value: closing_date, operator: ">"});
        openCurrentFilter.push({
            field: "transferdate",
            value: closing_date,
            operator: "<"
        });
        return {dateFilters, openCurrentFilter};
    }

    fields = {
        "roundings": {
            expression: "SUM(IF({{total}} > 0, IF(({{total}} % 5) >= 2.5 , FLOOR({{total}} / 5) * 5 + 5 , FLOOR({{total}} / 5) * 5) , IF((ABS({{total}}) % 5) >= 2.5 , FLOOR(ABS({{total}}) / 5) * 5 + 5 , FLOOR(ABS({{total}}) / 5) * 5) * -1) - {{total}})"
        },
        "total_count": {
            expression: "COUNT({{id}})"
        },
        "total": {
            expression: "SUM(ROUND(CAST({{total}} AS DECIMAL(13,4))))"
        },
    }

    loadFormData = debounce(() => {
        return new Promise((resolve) => {
            let till = this.props.till;
            let values = JSON.parse(JSON.stringify(this.props.values));
            let initialValues = JSON.parse(JSON.stringify(this.props.initialValues));
            values.till = till;
            initialValues.till = till;
            let closings;
            let initialClosings;
            if (!values.closings) {
                closings = this.props.till.moneyrepositories.map((moneyrepository) => {
                    return {moneyrepository};
                }).sort((a, b) => {
                    a = a?.moneyrepository?.component_type;
                    b = b?.moneyrepository?.component_type;
                    if (a + "" === "" + b) return 0;
                    return a < b ? -1 : 1;
                });
                values.vatrate_closings = this.props.till.vatrates.map((vatrate) => {
                    return {vatrate};
                });
                initialValues.vatrate_closings = JSON.parse(JSON.stringify(values.vatrate_closings));
                values.closings = closings;
                initialClosings = initialValues.closings = JSON.parse(JSON.stringify(closings));
            } else {
                closings = values.closings;
                initialClosings = initialValues.closings;
            }

            const {dateFilters, openCurrentFilter} = this.getDateFilters({...this.props, values});

            this.vatrateCorrectionRequest && this.vatrateCorrectionRequest.abort();
            if (!values[this.props.componentConfig.idfield]) {
                this.vatrateCorrectionRequest = loadComponent("componentlist", {
                    component: "TillCorrectionVatrate",
                    itemsperpage: 0,
                    filter: [
                        [...dateFilters].map((filter) => {
                            filter = {...filter};
                            filter.field = "correction.transaction_date";
                            return filter;
                        }),
                        {field: "correction.till", value: values.till.id}
                    ]
                });
                this.vatrateCorrectionRequest.then((res) => {
                    let vats = {};
                    let total = 0;
                    res.items.forEach((correction) => {
                        vats[correction.vatrate.id] = (vats[correction.vatrate.id] || 0) + parseInt(correction.amount);
                        total = total + parseInt(correction.amount);
                    })
                    values.vatrate_closings.forEach((closing) => {
                        if (vats[closing.vatrate.id] !== null && vats[closing.vatrate.id] !== undefined) {
                            closing.storno_amount = vats[closing.vatrate.id];
                        }
                    });
                    initialValues.vatrate_closings.forEach((closing) => {
                        if (vats[closing.vatrate.id] !== null && vats[closing.vatrate.id] !== undefined) {
                            closing.storno_amount = vats[closing.vatrate.id];
                        }
                    });
                    values.total_storno = total;
                    initialValues.total_storno = total;
                });
                initialValues.closing_number = values.closing_number = Number(this.props.lastClosingNumber) + 1;
            }

            this.vouchersRequest && this.vouchersRequest.abort();
            this.vouchersRequest = sendRequest({
                component: "Voucher",
                option: "componentlist",
                view: ["amount"],
                itemsperpage: 0,
                filter: [{
                    field: "used_invoice",
                    operator: "isnull"
                },{
                    field: "moneyrepository",
                    value: this.props.till.moneyrepositories.map((m) => m.repository_id),
                    operator: "IN"
                },...dateFilters.reduce((all, current) => {
                    let filter = {...current};
                    filter.field = "used";
                    return [...all,filter];
                }, [])],
                group: {
                    by: ["moneyrepository"],
                    fields: {
                        amount: {
                            type: "SUM"
                        }
                    }
                },
            });
            this.vouchersRequest.then((response) => {
                initialValues.voucher_total_check = values.voucher_total_check = response?.items?.reduce((all, {amount}) => all + parseInt(amount || "0"), 0) || 0;
            });

            this.vouchersRequest.then(() => {
                const requests = closings.reduce((all, closing, index) => {
                    const id = "moneyRepository" + String(closing.moneyrepository.repository_id) + "Request";
                    if (isFalse(closing.moneyrepository.virtual)) {
                        this?.[id + "open"]?.abort();
                        this[id + "open"] = sendRequest({
                            option: "componentlist",
                            component: "MoneyTransfer",
                            filter: [
                                {
                                    field: "moneyrepository",
                                    value: closing.moneyrepository.repository_id
                                },
                                ...openCurrentFilter
                            ],
                            aggregations: {
                                fields: {total: this.fields.total}
                            }
                        });
                        this[id + "open"].then((response) => {
                            initialClosings[index].open = closings[index].open = response.aggregations?.[0]?.total || 0;
                        });
                        all = [...all, this[id + "open"]];
                        this?.[id + "rounding"]?.abort();
                        this[id + "rounding"] = sendRequest({
                            option: "componentlist",
                            component: "MoneyTransfer",
                            filter: [
                                {
                                    field: "moneyrepository",
                                    value: closing.moneyrepository.repository_id
                                },
                                ...dateFilters
                            ],
                            aggregations: {
                                fields: {roundings: this.fields.roundings}
                            }
                        });
                        this[id + "rounding"].then((response) => {
                            initialValues.rounding_check = values.rounding_check = response.aggregations?.[0]?.roundings || 0;
                        });
                        all = [...all, this[id + "rounding"]];
                    }
                    Object.entries({
                        normal: {
                            filter: {
                                field: "invoice_count",
                                value: 0
                            },
                            call: (response) => {
                                initialClosings[index].real_amount = closings[index].real_amount = response.aggregations?.[0].total || 0;
                            }
                        },
                        invoice: {
                            filter: {
                                field: "invoice_count",
                                value: 0,
                                operator: ">"
                            },
                            call: (response) => {
                                if (closing.moneyrepository.component_type === "CreditCardTerminal") {
                                    initialClosings[index].invoice_total = closings[index].invoice_total = response.aggregations?.[0].total || 0;
                                }
                                initialClosings[index].invoice_total_check = closings[index].invoice_total_check = response.aggregations?.[0].total || 0;
                            }
                        },
                    }).forEach(([key, settings]) => {
                        const requestId = id + key;
                        this?.[requestId]?.abort();
                        this[requestId] = sendRequest({
                            option: "componentlist",
                            component: "MoneyTransfer",
                            filter: [
                                ...list(settings.filter),
                                {
                                    field: "moneyrepository",
                                    value: closing.moneyrepository.repository_id
                                },
                                ...dateFilters
                            ],
                            aggregations: {
                                fields: {total: this.fields.total}
                            }
                        });
                        this[requestId].then((response) => {
                            settings.call(response);
                        });
                        all = [...all, this[requestId]];
                    });
                    return all;
                }, []);
                Promise.all(requests).then(() => {
                    closings.forEach((closing, index) => {
                        initialClosings[index].current = closings[index].current = parseFloat(closings[index].open || "0") + parseFloat(closings[index].real_amount || "0") + parseFloat(closings[index].invoice_total_check || "0");
                    });
                    values.closings = closings;
                    initialValues.closings = initialClosings;
                    //this.props.setInitialValues({...values});
                    //this.props.setFormValues({[this.props.componentConfig.idfield]: values[this.props.componentConfig.idfield]});
                    this.props.setFormValues({...values});
                    this.props.setInitialValues({...initialValues});
                    //this.props.setInitialValues({});
                    resolve();
                })
            });
        });
    }, 300);

    saveForm = async (event) => {
        await this.props.saveForm(event);
        this.list && this.list.refresh();
    }

    getFieldProps = (field) => {
        const props = this.props.getFieldProps(field);
        if (this.props.values[this.props.componentConfig.idfield]) {
            props.disabled = true;
        }
        return props;
    }

    render() {

        const List = this.props.list || Box;
        const Editor = this.props.editor || BoxEditor;

        return (
            <Fragment>
                <List
                    {...this.props}
                    theme="default"
                    header="Zárások"
                    footer={[
                        <Button
                            key="0"
                            color="default"
                            disabled={this.state.isLoading || this.props.isLoading}
                            onClick={this.pristineForm}
                        >
                            Új
                        </Button>
                    ]}
                >
                    <TillClosingList
                        ref={(el) => this.list = el}
                        {...this.props}
                        disabled={this.props.disabled || this.state.isLoading || this.props.isLoading}
                        isLoading={this.state.isLoading || this.props.isLoading}
                        loadForm={this.loadForm}
                        getFieldProps={this.getFieldProps}
                    />
                </List>
                <Editor
                    {...this.props}
                    disabled={this.props.disabled || this.state.isLoading || this.props.isLoading}
                    isLoading={this.state.isLoading || this.props.isLoading}
                    ref={(editor) => this.editor = editor}
                    theme="default"
                    header="Zárás"
                    footer={<this.props.saveButton />}
                >
                    <TillClosingForm
                        {...this.props}
                        {...this.getDateFilters({...this.props})}
                        disabled={this.props.disabled || this.state.isLoading || this.props.isLoading}
                        isLoading={this.state.isLoading || this.props.isLoading}
                        getFieldProps={this.getFieldProps}
                        loadFormData={this.loadFormData}
                    />
                </Editor>
            </Fragment>
        );
    }
}

const actionValidation = async ({data, componentConfig}): Promise<ValidationType> => {
    let messages = null;
    messages = await validations(
        messages,
        data,
        [
            // moneyrepository closings validations
            ...Object.keys(data?.closings || {}).reduce((all, idx) => ([
                ...all,
                {
                    field: "closings." + idx + ".storno",
                    validation: ({value}) => is_numeric(value),
                    type: ({valid}) => valid ? "valid" : "error",
                    message: ({valid}) => !valid ? "Kérlek töltsd ki!" : true
                },
                {
                    field: "closings." + idx + ".storno",
                    validation: ({data}) => {
                        let closing = {...data.closings[idx]};
                        if (closing.moneyrepository.component_type === "CashRepository") {
                            if (closing.storno % 5 !== 0) {
                                return false;
                            }
                        }
                        return true;
                    },
                    type: ({valid}) => valid ? "valid" : "error",
                    message: ({valid}) => !valid ? "Nem kerekített érték!" : true
                },
                {
                    field: "closings." + idx + ".storno",
                    validation: ({data}) => {
                        const sum = data.closings.reduce((sum, closing) => sum + parseInt(closing.storno), 0);
                        return Math.abs(sum) < 3;
                    },
                    type: ({valid}) => valid ? "valid" : "error",
                    message: ({valid}) => !valid ? "Hibás adat!" : true
                },
                ...(["current","amount","total","invoice_total"].map((field) => ({
                    field: "closings." + idx + "." + field,
                    validation: ({data}) => {
                        let closing = {...data.closings[idx]};
                        if (closing.moneyrepository.component_type === "CashRepository") {
                            if (closing[field] % 5 !== 0) {
                                return false;
                            }
                        }
                        return true;
                    },
                    type: ({valid}) => valid ? "valid" : "error",
                    message: ({valid}) => !valid ? "Nem kerekített érték!" : true
                }))),
                ...(["current","amount","total","invoice_total"].map((field) => ({
                    field: "closings." + idx + "." + field,
                    validation: ({value, data}) => {
                        let closing = {...data.closings[idx]};
                        closing.total = parseInt(closing.total) + parseInt(closing.storno);
                        if (closing.moneyrepository.component_type === "CreditCardTerminal") {
                            closing.total += parseInt(closing.invoice_total_check);
                        }
                        if (!is_numeric(value)) {
                            return false;
                        }
                        const calcOccurrence = ({current, amount, total}) => {
                            const counts = compressArray([current, amount, total]);
                            const ret = {};
                            Object.keys(counts).map((key) => {
                                if (counts[key].value + "" === "" + current) {
                                    ret.current = counts[key].count;
                                }
                                if (counts[key].value + "" === "" + amount) {
                                    ret.amount = counts[key].count;
                                }
                                if (counts[key].value + "" === "" + total) {
                                    ret.total = counts[key].count;
                                }
                            });
                            return ret;
                        }
                        const occurrence = calcOccurrence(closing);
                        switch (field) {
                            case "current":
                                return occurrence.current > 1;
                            case "amount":
                                return occurrence.amount > 1;
                            case "total":
                                return occurrence.total > 1;
                            case "invoice_total":
                                let valid = !isNaN(closing.invoice_total) && occurrence.total > 1;
                                if (closing.moneyrepository.component_type !== "CreditCardTerminal" && !data[componentConfig.idfield]) {
                                    valid = valid && hufRound(closing.invoice_total) === hufRound(closing.invoice_total_check);
                                }
                                return valid;
                        }
                    },
                    type: ({valid}) => valid ? "valid" : "error",
                    message: ({valid}) => !valid ? "Hibás adat!" : true
                })))
            ]), []),
            // till closing validations
            {
                field: ["voucher_total","rounding_total","total_storno","transaction_count","total"],
                validation: ({value}) => is_numeric(value),
                type: ({valid}) => valid ? "valid" : "error",
                message: ({valid}) => !valid ? "Kérlek töltsd ki!" : true
            },
            {
                field: "closing_number",
                validation: ({value}) => !empty(value),
                type: ({valid}) => valid ? "valid" : "error",
                message: ({valid}) => !valid ? "Kérlek töltsd ki!" : true
            },
            /*{
                field: "total",
                validation: ({value, data}) => {
                    return (data?.closings || []).reduce((sum, closing) => {
                        return (closing.moneyrepository.component_type === "CreditCardTerminal"
                            ?
                            parseInt(closing.open || "0") + parseInt(closing.real_amount || "0")
                            :
                            parseInt(closing.open || "0") + parseInt(closing.pristine || "0") + parseInt(closing.invoice_total || "0")) + sum;
                    }, 0) + "" ===  "" + value;
                },
                type: ({valid}) => valid ? "valid" : "error",
                message: ({valid, data}) => !valid ? "A forgalom hibás!" : true
            },*/
            /*{
                field: "rounding_total",
                validation: ({value, data}) => data.rounding_check + "" === "" + value,
                type: ({valid}) => valid ? "valid" : "warning",
                message: ({valid}) => !valid ? "A kerekítés problémás lehet. A kerekítés összege: " + CurrencyFormatter(data.rounding_check).props.children + "!" : true
            },*/
            {
                field: "rounding_total",
                validation: ({value, data}) => is_numeric(data.rounding_check),
                type: ({valid}) => valid ? "valid" : "error",
                message: ({valid}) => !valid ? "Kérlek töltsd ki!" : true
            },
            {
                field: "voucher_total",
                validation: ({value, data}) => data.voucher_total_check + "" === "" + value,
                type: ({valid}) => valid ? "valid" : "error",
                message: ({valid, data}) => !valid ? "Az utalványok összege: " + CurrencyFormatter(data.voucher_total_check).props.children + "!" : true
            },
            {
                field: /^(closing_number)$/i,
                validation: ({value}) => !isEmpty(value),
                type: ({valid}) => valid ? "valid" : "error",
                message: ({valid}) => !valid ? "Kérlek töltsd ki!" : true
            },
            {
                field: "total_storno",
                validation: ({value, data}) => Object.values(data?.closings || {}).reduce((sum, closing) => parseInt(sum || "0") + parseInt(closing.storno || "0"), 0) > 0 ? parseInt(data.total_storno || "0") > 0 : true,
                type: ({valid}) => valid ? "valid" : "error",
                message: ({valid, data}) => !valid ? "Ha volt Bankkártyás sztornó akkor nem lehet nulla!" : true
            },
            // vatrate closing validations
            ...Object.keys(data?.vatrate_closings || {}).reduce((all, idx) => {
                const amount = !data.vatrate_closings?.length ? undefined : data.vatrate_closings.map((vatrate_closing) => vatrate_closing.amount).reduce((sum, amount) => parseFloat(sum || "0") + parseFloat(amount || "0"));
                const storno_amount = !data.vatrate_closings?.length ? undefined : data.vatrate_closings.map((vatrate_closing) => vatrate_closing.storno_amount).reduce((sum, storno_amount) => parseFloat(sum || "0") + parseFloat(storno_amount || "0"));

                return [
                    ...all,
                    ...(["amount","storno_amount"].map((field) => ({
                        field: "vatrate_closings." + idx + "." + field,
                        validation: ({value}) => is_numeric(value),
                        type: ({valid}) => valid ? "valid" : "error",
                        message: ({valid}) => !valid ? "Kérlek töltsd ki!" : true
                    }))),
                    ...(["amount","storno_amount"].map((field) => ({
                        field: "vatrate_closings." + idx + "." + field,
                        validation: ({data}) => {
                            switch (field) {
                                case "amount":
                                    return amount + "" === "" + data.total;
                                case "storno_amount":
                                    return storno_amount + "" === "" + data.total_storno;
                            }
                        },
                        type: ({valid}) => valid ? "valid" : "error",
                        message: ({valid}) => !valid ? "Hibás adat!" : true
                    })))
                ]
            }, [])
        ]
    );
    return messages;
};

export const TillLastClosingDate = context((props) => {
    const [lastClosingDate, setLastClosingDate] = useState(null);
    const [lastClosingNumber, setLastClosingNumber] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
        setIsLoading(true);
        const closingDateRequest = sendRequest({
            component: "TillClosing",
            option: "componentlist",
            view: ["closing_date","closing_number"],
            page: 1,
            itemsperpage: 1,
            filter: [{field: "till", value: props.till.id}],
            order: [{field: "closing_date", direction: "DESC"}],
        });
        closingDateRequest.then((response) => {
            setLastClosingDate(response?.items?.[0]?.closing_date);
            setLastClosingNumber(response?.items?.[0]?.closing_number);
            setIsLoading(false);
        });
        return () => {
            closingDateRequest.abort();
        }
    }, [props.till, props.values?.[props.componentConfig.idfield]]);
    if (isLoading) {
        return <ContentLoader />;
    }
    return <PropsProxy {...props} lastClosingDate={lastClosingDate} lastClosingNumber={lastClosingNumber}/>
}, {needle: ["till"]});

export default (props) => (
    <ContextBased needle="till">
        <ComponentEditor
            saveForm={async ({requestData, componentConfig}) => {
                requestData.data = await referenceActionData(requestData.data, componentConfig);
                return postData(requestData);
            }}
            dirty={({values, componentConfig, dirty}) => {
                return !values[componentConfig.idfield] ? dirty : false;
            }}
            componentName="TillClosing"
            actionValidation={actionValidation}
            {...props}
        >
            <TillLastClosingDate>
                <TillClosingEditor />
            </TillLastClosingDate>
        </ComponentEditor>
    </ContextBased>
);